import { useMemo, useState } from "react";
import { AlertTriangle, Archive, ChevronDown, ChevronRight, Download, FileText, Folder, FolderPlus, FolderUp, Loader2, MessageSquarePlus, MoreHorizontal, Pencil, Trash2, Upload } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { DocViewModal } from "@/components/common/DocViewModal";
import type { DocPanelTab } from "@/components/shared/DocPanel";
import { ErrorBanner } from "@/components/common/ErrorBanner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { inferDocumentIngestStage } from "@/hooks/queries/ingestStage";
import { useInvalidateStandaloneDocuments } from "@/hooks/queries/useDocuments";
import { useProjectFileUpload } from "@/hooks/useProjectFileUpload";
import { api, type DocumentRow, type FolderRow, type VersionRow } from "@/lib/api";
import { DOCUMENT_ACCEPT_ATTR } from "@/lib/documentTypes";
import { cn } from "@/lib/utils";

interface ProjectFileTreeProps {
  projectId: string;
  documents: DocumentRow[];
  folders: FolderRow[];
  onRefresh: () => Promise<void>;
}

interface TreeFolder extends FolderRow {
  children: TreeFolder[];
  docs: DocumentRow[];
}

type RenameTarget = { type: "folder" | "document"; id: string; value: string };

function DocIngestBadge({ doc }: { doc: DocumentRow }) {
  const stage = inferDocumentIngestStage(doc);
  const conversionFailed = doc.conversion_failed ?? false;

  const stageClassName = stage === "error" || stage === "index_failed" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground";

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${stageClassName}`}>
        {stage === "processing" || stage === "indexing" ? <Loader2 className="size-3 animate-spin" /> : null}
        {stage === "processing"
          ? "Processing…"
          : stage === "indexing"
            ? "Indexing…"
            : stage === "ready"
              ? "Ready"
              : stage === "index_failed"
                ? "Not searchable"
                : "Failed"}
      </span>
      {conversionFailed ? (
        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs bg-warning/10 text-warning">
          <AlertTriangle className="size-3" />
          Preview unavailable
        </span>
      ) : null}
    </div>
  );
}

function buildTree(folders: FolderRow[], documents: DocumentRow[]) {
  const byId = new Map(folders.map((folder) => [folder.id, { ...folder, children: [] as TreeFolder[], docs: [] as DocumentRow[] } satisfies TreeFolder]));
  const roots: TreeFolder[] = [];

  byId.forEach((folder) => {
    const parentId = folder.parent_folder_id;
    const parent = parentId ? byId.get(parentId) : null;
    if (parent) {
      parent.children.push(folder);
    } else {
      roots.push(folder);
    }
  });

  documents.forEach((doc) => {
    const parent = doc.folder_id ? byId.get(doc.folder_id) : null;
    if (parent) {
      parent.docs.push(doc);
    }
  });

  const rootDocs = documents.filter((doc) => !doc.folder_id || !byId.has(doc.folder_id));
  const sortFolder = (folder: TreeFolder) => {
    folder.children.sort((a, b) => a.name.localeCompare(b.name));
    folder.docs.sort((a, b) => a.filename.localeCompare(b.filename));
    folder.children.forEach(sortFolder);
  };
  roots.sort((a, b) => a.name.localeCompare(b.name)).forEach(sortFolder);
  rootDocs.sort((a, b) => a.filename.localeCompare(b.filename));

  return { roots, rootDocs };
}

function downloadBlob(response: Response, fallbackName: string) {
  void response.blob().then((blob) => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = fallbackName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(href), 100);
  });
}

export function ProjectFileTree({ projectId, documents, folders, onRefresh }: ProjectFileTreeProps) {
  const navigate = useNavigate();
  const { roots, rootDocs } = useMemo(() => buildTree(folders, documents), [documents, folders]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(folders.map((folder) => folder.id)));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rename, setRename] = useState<RenameTarget | null>(null);
  const [versionsFor, setVersionsFor] = useState<string | null>(null);
  const [versions, setVersions] = useState<Record<string, VersionRow[]>>({});
  const [openDoc, setOpenDoc] = useState<DocPanelTab | null>(null);
  const { busy, setBusy, busyLabel, error, setError, dragOver, uploadFiles, handleDrop, handleSectionDragEnter, handleSectionDragLeave, handleSectionDragOver } = useProjectFileUpload({ projectId, onRefresh });

  const selectedDocs = documents.filter((doc) => selected.has(doc.id));

  const invalidateStandaloneDocs = useInvalidateStandaloneDocuments();

  const refreshAfterStandaloneDelete = async () => {
    await Promise.all([onRefresh(), invalidateStandaloneDocs()]);
  };

  const saveRename = async () => {
    if (!rename) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (rename.type === "folder") {
        await api.projects.updateFolder(projectId, rename.id, { name: rename.value.trim() });
      } else {
        await api.projects.renameDocument(projectId, rename.id, rename.value.trim());
      }
      setRename(null);
      await onRefresh();
    } catch (renameError) {
      setError(renameError instanceof Error ? renameError.message : "Rename failed.");
    } finally {
      setBusy(false);
    }
  };

  const createFolder = async (parentFolderId: string | null) => {
    const name = window.prompt("Folder name");
    if (!name?.trim()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.projects.createFolder(projectId, { name: name.trim(), parent_folder_id: parentFolderId });
      await onRefresh();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Folder could not be created.");
    } finally {
      setBusy(false);
    }
  };

  const deleteFolder = async (folderId: string) => {
    if (!window.confirm("Delete this folder? Documents inside it move back to the project root.")) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.projects.deleteFolder(projectId, folderId);
      await onRefresh();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Folder could not be deleted.");
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    if (!selectedDocs.length || !window.confirm(`Delete ${selectedDocs.length} selected document(s)?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await Promise.all(selectedDocs.map((doc) => api.documents.deleteStandalone(doc.id)));
      setSelected(new Set());
      await refreshAfterStandaloneDelete();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const newChatFromDocument = async (doc: DocumentRow) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.chat.create(projectId, doc.id);
      await navigate({ to: "/projects/$id/assistant/chat/$chatId", params: { id: projectId, chatId: id } });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not start a chat from this document.");
    } finally {
      setBusy(false);
    }
  };

  const deleteDocument = async (doc: DocumentRow) => {
    if (!window.confirm(`Delete ${doc.display_name ?? doc.filename}?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.documents.deleteStandalone(doc.id);
      setSelected((current) => {
        const next = new Set(current);
        next.delete(doc.id);
        return next;
      });
      await refreshAfterStandaloneDelete();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const downloadDocuments = async (docs: DocumentRow[]) => {
    if (!docs.length) {
      return;
    }
    setError(null);
    try {
      if (docs.length === 1) {
        const result = await api.documents.url(docs[0].id);
        window.open(result.url, "_blank", "noopener,noreferrer");
        return;
      }
      const response = await api.documents.downloadZipResponse(docs.map((doc) => doc.id));
      if (response.ok) {
        downloadBlob(response, "documents.zip");
      } else {
        setError(await response.text());
      }
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "Download failed.");
    }
  };

  const bulkDownload = async () => {
    await downloadDocuments(selectedDocs);
  };

  const downloadDocument = async (doc: DocumentRow) => {
    await downloadDocuments([doc]);
  };

  const toggleVersions = async (doc: DocumentRow) => {
    const next = versionsFor === doc.id ? null : doc.id;
    setVersionsFor(next);
    if (next && !versions[doc.id]) {
      try {
        const result = await api.documents.versions(doc.id);
        setVersions((current) => ({ ...current, [doc.id]: result.versions }));
      } catch (versionError) {
        setError(versionError instanceof Error ? versionError.message : "Version history could not be loaded.");
      }
    }
  };

  const renameVersion = async (doc: DocumentRow, version: VersionRow) => {
    const displayName = window.prompt("Version display name", version.display_name ?? "");
    if (displayName === null) {
      return;
    }
    setError(null);
    try {
      const updated = await api.documents.renameVersion(doc.id, version.id, displayName.trim() || null);
      setVersions((current) => ({ ...current, [doc.id]: (current[doc.id] ?? []).map((item) => (item.id === updated.id ? updated : item)) }));
    } catch (renameError) {
      setError(renameError instanceof Error ? renameError.message : "Version could not be renamed.");
    }
  };

  const renderDocument = (doc: DocumentRow, depth = 0) => (
    <div key={doc.id}>
      <div
        draggable
        onDragStart={(event) => event.dataTransfer.setData("application/json", JSON.stringify({ type: "document", id: doc.id }))}
        className="group flex min-h-10 items-center gap-2 border-b px-2 text-sm hover:bg-muted/60"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        <input
          type="checkbox"
          checked={selected.has(doc.id)}
          onChange={(event) =>
            setSelected((current) => {
              const next = new Set(current);
              if (event.target.checked) {
                next.add(doc.id);
              } else {
                next.delete(doc.id);
              }
              return next;
            })
          }
          aria-label={`Select ${doc.display_name ?? doc.filename}`}
        />
        <button type="button" onClick={() => void toggleVersions(doc)} className="text-muted-foreground">
          {versionsFor === doc.id ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <FileText className="size-4 shrink-0 text-primary" />
        {rename?.type === "document" && rename.id === doc.id ? (
          <Input
            value={rename.value}
            autoFocus
            onChange={(event) => setRename({ ...rename, value: event.target.value })}
            onBlur={() => void saveRename()}
            onKeyDown={(event) => {
              if (event.key === "Enter") void saveRename();
              if (event.key === "Escape") setRename(null);
            }}
            className="h-8"
          />
        ) : (
          <button type="button" onClick={() => setOpenDoc({ documentId: doc.id, filename: doc.filename, versionId: doc.current_version_id ?? undefined, versionNumber: doc.latest_version_number ?? undefined, kind: "document" })} className="min-w-0 flex-1 truncate text-left">
            {doc.display_name ?? doc.filename}
          </button>
        )}
        <DocIngestBadge doc={doc} />
        <RowMenu
          onDelete={() => void deleteDocument(doc)}
          onDownload={() => void downloadDocument(doc)}
          onNewChat={() => void newChatFromDocument(doc)}
          onRename={() => setRename({ type: "document", id: doc.id, value: doc.display_name ?? doc.filename })}
          onView={() => setOpenDoc({ documentId: doc.id, filename: doc.filename, versionId: doc.current_version_id ?? undefined, versionNumber: doc.latest_version_number ?? undefined, kind: "document" })}
        />
      </div>
      {versionsFor === doc.id ? (
        <div className="border-b bg-muted/25 py-2 text-xs" style={{ paddingLeft: `${depth * 20 + 52}px` }}>
          {(versions[doc.id] ?? []).map((version) => (
            <div key={version.id} className="flex items-center gap-2 py-1 pr-3">
              <span className="rounded-md bg-background px-2 py-0.5">V{version.version_number}</span>
              <button type="button" className="min-w-0 flex-1 truncate text-left" onClick={() => setOpenDoc({ documentId: doc.id, filename: version.display_name ?? doc.filename, versionId: version.id, versionNumber: version.version_number, kind: "document" })}>
                {version.display_name ?? doc.filename}
              </button>
              {version.index_status && version.index_status !== "indexed" && (
                <span className={`rounded px-1.5 py-0.5 text-xs ${version.index_status === "failed" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}>
                  {version.index_status === "pending" ? "indexing…" : version.index_status}
                </span>
              )}
              <Button type="button" variant="ghost" size="sm" onClick={() => void renameVersion(doc, version)}>
                <Pencil className="size-3" />
                Rename
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  const renderFolder = (folder: TreeFolder, depth = 0) => {
    const isOpen = expanded.has(folder.id);
    return (
      <div key={folder.id}>
        <div
          draggable
          onDragStart={(event) => event.dataTransfer.setData("application/json", JSON.stringify({ type: "folder", id: folder.id }))}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => { event.stopPropagation(); void handleDrop(event, folder.id); }}
          className="group flex min-h-10 items-center gap-2 border-b bg-card px-2 text-sm hover:bg-muted/60"
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          <button
            type="button"
            onClick={() =>
              setExpanded((current) => {
                const next = new Set(current);
                if (next.has(folder.id)) next.delete(folder.id);
                else next.add(folder.id);
                return next;
              })
            }
            className="text-muted-foreground"
          >
            {isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
          <Folder className="size-4 shrink-0 text-warning" />
          {rename?.type === "folder" && rename.id === folder.id ? (
            <Input
              value={rename.value}
              autoFocus
              onChange={(event) => setRename({ ...rename, value: event.target.value })}
              onBlur={() => void saveRename()}
              onKeyDown={(event) => {
                if (event.key === "Enter") void saveRename();
                if (event.key === "Escape") setRename(null);
              }}
              className="h-8"
            />
          ) : (
            <span className="min-w-0 flex-1 truncate">{folder.name}</span>
          )}
          <FolderMenu onCreate={() => void createFolder(folder.id)} onRename={() => setRename({ type: "folder", id: folder.id, value: folder.name })} onDelete={() => void deleteFolder(folder.id)} />
        </div>
        {isOpen ? (
          <>
            {folder.children.map((child) => renderFolder(child, depth + 1))}
            {folder.docs.map((doc) => renderDocument(doc, depth + 1))}
          </>
        ) : null}
      </div>
    );
  };

  return (
    <section
      className={cn("relative min-h-0 rounded-md border bg-background", dragOver && "ring-2 ring-primary/50")}
      onDragEnter={handleSectionDragEnter}
      onDragLeave={handleSectionDragLeave}
      onDragOver={handleSectionDragOver}
      onDrop={(event) => void handleDrop(event, null)}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-primary/60 bg-primary/5">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <FolderUp className="size-5" />
            Drop folder or files to upload
          </div>
        </div>
      )}
      <header
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => { event.stopPropagation(); void handleDrop(event, null); }}
        className={cn("flex flex-wrap items-center gap-2 border-b bg-card px-3 py-2", busy && "opacity-70")}
      >
        <Button type="button" variant="outline" size="sm" onClick={() => void createFolder(null)}>
          <FolderPlus className="size-4" />
          Folder
        </Button>
        <label className="inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border bg-background px-3 text-sm font-medium hover:bg-accent">
          <Upload className="size-4" />
          Upload
          <input type="file" multiple accept={DOCUMENT_ACCEPT_ATTR} className="sr-only" onChange={(event) => { if (event.target.files) { void uploadFiles(event.target.files, null); } event.target.value = ""; }} />
        </label>
        <Button type="button" variant="outline" size="sm" disabled={!selectedDocs.length} onClick={() => void bulkDownload()}>
          {selectedDocs.length > 1 ? <Archive className="size-4" /> : <Download className="size-4" />}
          Download
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={!selectedDocs.length} onClick={() => void deleteSelected()}>
          <Trash2 className="size-4" />
          Delete
        </Button>
        {busy ? (
          <span className="ml-auto flex items-center gap-2 tabular-nums text-xs text-primary">
            <Loader2 className="size-4 animate-spin" />
            {busyLabel}
          </span>
        ) : (
          <span className="ml-auto text-xs text-muted-foreground">Drop files or folders here</span>
        )}
      </header>
      <div className="p-3">
        <ErrorBanner message={error} />
      </div>
      <div className="border-t">
        {roots.map((folder) => renderFolder(folder))}
        {rootDocs.map((doc) => renderDocument(doc))}
        {!roots.length && !rootDocs.length ? <p className="p-6 text-center text-sm text-muted-foreground">No project documents yet.</p> : null}
      </div>
      <DocViewModal tab={openDoc} onClose={() => setOpenDoc(null)} />
    </section>
  );
}

function RowMenu({ onDelete, onDownload, onNewChat, onRename, onView }: { onDelete: () => void; onDownload: () => void; onNewChat: () => void; onRename: () => void; onView: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="size-8 opacity-70 group-hover:opacity-100">
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onView}>
          <FileText className="size-4" />
          Open
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onNewChat}>
          <MessageSquarePlus className="size-4" />
          New chat from this document
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onRename}>
          <Pencil className="size-4" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDownload}>
          <Download className="size-4" />
          Download
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDelete} className="text-destructive">
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FolderMenu({ onCreate, onRename, onDelete }: { onCreate: () => void; onRename: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="size-8 opacity-70 group-hover:opacity-100">
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onCreate}>
          <FolderPlus className="size-4" />
          New subfolder
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onRename}>
          <Pencil className="size-4" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDelete} className="text-destructive">
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
